import { useEffect, useState } from "react";
import {
  X, PenSquare, Loader2, CheckCircle2, RefreshCw, AlertCircle, ShieldCheck,
} from "lucide-react";
import { useWriteQueue } from "@/lib/writeQueue";
import { checkCounts, checkLabel, checkTone } from "@/lib/autoFactCheck";
import { tr } from "@/lib/i18n";

const TONE_CLASS = {
  good: "text-emerald-700",
  warn: "text-amber-700",
  bad: "text-red-700",
  neutral: "text-[var(--ink-muted)]",
};

/**
 * Floating progress bar for the "Write selected ideas" batch.
 *
 * Renders as a small pill anchored bottom-left (bottom-right is
 * taken by sonner toasts). Visible whenever a batch is active OR
 * has recently completed. Auto-dismisses ~5s after the last item
 * finishes so it doesn't linger past its usefulness — editors can
 * also close it manually. Any in-flight batch keeps it pinned.
 *
 * When a batch has errored entries the auto-dismiss is skipped so the
 * editor can see the Retry buttons; the floater stays open until they
 * either retry, succeed, or dismiss it manually.
 *
 * Automatic fact checks (2026-10-01): when the batch was written with
 * "Fact check after writing", each written article shows its check
 * result here. The batch only counts as complete once every check has
 * finished, and it never auto-hides — the scores are the point. Each
 * row opens the article in a new tab (this floater sits outside the
 * router, and a full page load would drop the in-memory queue).
 */
export default function WriteProgressFloater() {
  const {
    active, total, completed, currentHeadline, items, dismiss, retryEntry,
  } = useWriteQueue();
  const [autoHiding, setAutoHiding] = useState(false);

  const writesDone = active && total > 0 && completed === total;
  const checks = checkCounts(items);
  const done = writesDone && checks.running === 0;
  const erroredItems = items.filter((i) => i.status === "error");
  const errored = erroredItems.length;
  const checkedItems = items.filter((i) => i.check);

  // Auto-hide 5s after completion, but ONLY when nothing errored — a
  // failed write needs the editor's eye on the Retry button — and no
  // fact checks ran (their results stay up until dismissed).
  useEffect(() => {
    if (!done || errored > 0 || checks.total > 0) {
      setAutoHiding(false);
      return;
    }
    const t = setTimeout(() => {
      setAutoHiding(true);
      dismiss();
    }, 5000);
    return () => clearTimeout(t);
  }, [done, errored, checks.total, dismiss]);

  if (!active || autoHiding) return null;

  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div
      data-testid="write-progress-floater"
      className="fixed bottom-4 left-4 z-50 w-[360px] max-w-[90vw] bg-[var(--surface)] border border-[var(--border)] shadow-lg"
      role="status"
      aria-live="polite"
    >
      <div className="px-3 py-2.5 flex items-start gap-2">
        <span className="mt-0.5 text-[var(--accent)]">
          {done ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)]">
            {writesDone && !done ? (
              <ShieldCheck className="w-3 h-3" />
            ) : (
              <PenSquare className="w-3 h-3" />
            )}
            {done
              ? tr("Batch complete")
              : writesDone
              ? tr("Newsdesk is fact-checking")
              : tr("Newsdesk is writing")}
            <span className="ml-auto text-[var(--ink)]">
              {completed}/{total}
            </span>
          </div>
          {checks.total > 0 && (
            <div
              data-testid="write-progress-check-count"
              className="mt-0.5 text-[11px] text-[var(--ink-muted)]"
            >{tr("Fact checks: {value}/{total} finished{value2}", { value: checks.done + checks.error, total: checks.total, value2: checks.running > 0 ? tr(" · {running} running", { running: checks.running }) : "" })}
            </div>
          )}
          {!writesDone && currentHeadline && (
            <div
              className="mt-1 text-xs text-[var(--ink)] leading-snug truncate"
              title={currentHeadline}
            >
              {currentHeadline}
            </div>
          )}
          {done && errored > 0 && (
            <div className="mt-1 text-xs text-amber-700">
              {tr("{errored} failed — click Retry below", { errored })}</div>
          )}
          {done && errored === 0 && (
            <div className="mt-1 text-xs text-emerald-700">{tr("All done.")}</div>
          )}
        </div>
        {writesDone && (
          <button
            type="button"
            onClick={dismiss}
            data-testid="write-progress-dismiss"
            aria-label={tr("Dismiss progress bar")}
            className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {/* Slim progress line — the actual visual "bar". */}
      <div className="h-1 bg-[var(--secondary)]">
        <div
          className="h-full bg-[var(--accent)] transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      {/* Automatic fact-check results — one row per written article. */}
      {checkedItems.length > 0 && (
        <ul
          data-testid="write-progress-checks"
          className="max-h-[200px] overflow-auto border-t border-[var(--border)] divide-y divide-[var(--border)]"
        >
          {checkedItems.map((it) => {
            const tone = checkTone(it.check);
            return (
              <li key={it.ideaId} className="px-3 py-2 flex items-start gap-2">
                {it.check.status === "running" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--ink-muted)] mt-0.5 shrink-0" />
                ) : (
                  <ShieldCheck className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${TONE_CLASS[tone]}`} />
                )}
                <div className="flex-1 min-w-0">
                  <a
                    href={`/article/${it.articleId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`write-progress-check-link-${it.ideaId}`}
                    className="block text-xs text-[var(--ink)] leading-snug truncate hover:underline"
                    title={tr("{headline} — opens in a new tab", { headline: it.headline })}
                  >
                    {it.headline || tr("Article {slice}", { slice: String(it.articleId || "").slice(0, 8) })}
                  </a>
                  <div
                    data-testid={`write-progress-check-${it.ideaId}`}
                    className={`text-[11px] mt-0.5 line-clamp-2 ${TONE_CLASS[tone]}`}
                    title={it.check.status === "error" ? it.check.error : undefined}
                  >
                    {checkLabel(it.check)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {/* Errored-entries retry list — only rendered when the batch has
          at least one failure so the floater stays compact for the
          happy path. */}
      {errored > 0 && (
        <ul
          data-testid="write-progress-errors"
          className="max-h-[180px] overflow-auto border-t border-[var(--border)] divide-y divide-[var(--border)]"
        >
          {erroredItems.map((it) => (
            <li
              key={it.ideaId}
              className="px-3 py-2 flex items-start gap-2"
            >
              <AlertCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div
                  className="text-xs text-[var(--ink)] leading-snug truncate"
                  title={it.headline}
                >
                  {it.headline || tr("Idea {slice}", { slice: it.ideaId.slice(0, 8) })}
                </div>
                {it.status === "writing" ? (
                  <div className="text-[11px] text-[var(--ink-muted)] mt-0.5 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("Retrying…")}</div>
                ) : (
                  it.error && (
                    <div
                      className="text-[11px] text-red-700 mt-0.5 line-clamp-2"
                      title={it.error}
                    >
                      {it.error}
                    </div>
                  )
                )}
              </div>
              {it.status !== "writing" && (
                <button
                  type="button"
                  onClick={() => retryEntry(it.ideaId)}
                  data-testid={`write-progress-retry-${it.ideaId}`}
                  className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--accent)] hover:underline inline-flex items-center gap-1 shrink-0"
                >
                  <RefreshCw className="w-3 h-3" />{tr("Retry")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
